

































































































import {Component, Vue} from "vue-property-decorator";
import authService from "@/components/domains/auth/authService";
import {User} from "@/components/domains/auth/user";
import PasswordlessService from "@/components/domains/auth/passwordlessService";
import PasskeyService, {passkeyErrorMessage} from "@/components/domains/auth/passkeyService";
import {
  AuthPolicy,
  emailFieldError,
  loginScreen,
  LoginScreen,
  passwordRetireNotice,
  toAuthError,
} from "@/components/domains/auth/authPolicy";

const RESEND_COOLDOWN_S = 60;
const LOGIN_FAILED = 'ログインに失敗しました。';

@Component
export default class LoginForm extends Vue {
  name = "LoginForm";

  loginId = '';
  password = '';

  policy: AuthPolicy | null = null;

  // magic link
  phase: 'form' | 'sent' = 'form';
  magicEmail = '';
  sentTo = '';
  magicError = '';
  magicInfo = '';
  sending = false;
  cooldown = 0;
  timer: number | null = null;

  // password (secondary, while the magic link is available)
  passwordOpen = false;
  passwordError = '';
  submitting = false;

  // 指紋・顔でログイン (only when the backend offers it and the browser can)
  passkeySupported = PasskeyService.isSupported();
  passkeyBusy = false;
  passkeyError = '';

  get passkeyShown(): boolean {
    return !!this.screen && this.screen.passkey && this.passkeySupported;
  }

  get screen(): LoginScreen | null {
    return this.policy ? loginScreen(this.policy) : null;
  }

  get retireNotice(): string {
    return this.screen ? passwordRetireNotice(this.screen.passwordRetiresAt) : '';
  }

  mounted(): void {
    PasswordlessService.loadPolicy({fresh: true}).then((p) => (this.policy = p));
  }

  beforeDestroy(): void {
    this.stopTimer();
  }

  // Today's password login (unchanged, except that a retired-method message
  // from the backend is shown as-is instead of the generic one).
  login(): void {
    authService.login(this.loginId, this.password)
        .then((user: User) => {
          this.$router.push("/");
        })
        .catch((e) => {
          const err = toAuthError(e, LOGIN_FAILED);
          alert(/_RETIRED$/.test(err.reason) ? err.message : LOGIN_FAILED);
        });
  }

  loginInline(): void {
    this.passwordError = '';
    if (!this.loginId || !this.password) {
      this.passwordError = 'ログイン ID とパスワードを入力してください。';
      return;
    }
    this.submitting = true;
    authService.login(this.loginId, this.password)
        .then(() => {
          this.$router.push("/");
        })
        .catch((e) => {
          this.passwordError = toAuthError(e, LOGIN_FAILED).message;
        })
        .finally(() => {
          this.submitting = false;
        });
  }

  async loginWithPasskey(): Promise<void> {
    if (this.passkeyBusy) return;
    this.passkeyError = '';
    this.passkeyBusy = true;
    try {
      await PasskeyService.login();
    } catch (e) {
      this.passkeyError = passkeyErrorMessage(e, LOGIN_FAILED);
      this.passkeyBusy = false;
      return;
    }
    this.passkeyBusy = false;
    this.$router.push("/");
  }

  // ------------------------------------------------------------ magic link
  stopTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  startCooldown(): void {
    this.cooldown = RESEND_COOLDOWN_S;
    this.stopTimer();
    this.timer = window.setInterval(() => {
      this.cooldown -= 1;
      if (this.cooldown <= 0) this.stopTimer();
    }, 1000);
  }

  async send(address: string): Promise<boolean> {
    this.magicError = '';
    this.sending = true;
    try {
      await PasswordlessService.requestMagicLink(address);
    } catch (e) {
      this.magicError = toAuthError(e, 'メールを送れませんでした。しばらくしてから、もう一度お試しください。').message;
      return false;
    } finally {
      this.sending = false;
    }
    this.startCooldown();
    return true;
  }

  async sendMagicLink(): Promise<void> {
    this.magicInfo = '';
    const v = this.magicEmail.trim();
    const invalid = emailFieldError(v, true);
    if (invalid) {
      this.magicError = invalid;
      return;
    }
    if (await this.send(v)) {
      this.sentTo = v;
      this.phase = 'sent';
    }
  }

  async resend(): Promise<void> {
    this.magicInfo = '';
    if (this.cooldown > 0 || this.sending) return;
    if (await this.send(this.sentTo)) this.magicInfo = 'もう一度送りました。';
  }

  changeAddress(): void {
    this.phase = 'form';
    this.magicError = '';
    this.magicInfo = '';
  }
}
